import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import MedicalCertificateIcon from '../../assets/images/icons/prescription/medicalcertificate.svg';
import ExamIcon from '../../assets/images/icons/prescription/exam.svg';
import MedicineIcon from '../../assets/images/icons/prescription/medicine.svg';
import FormulaIcon from '../../assets/images/icons/prescription/formula.svg';
import MedicalReferralIcon from '../../assets/images/icons/prescription/medicalreferral.svg';
import MedicalReportIcon from '../../assets/images/icons/prescription/medicalreport.svg';
import { MdMenu } from 'react-icons/md';

import { ReactSVG } from 'react-svg';

import { get, isEmpty } from 'lodash';
import { FaPlus } from 'react-icons/fa';
import { StyledButton, StyledMenuButton, StyledMenuItem } from './styles';
import { Menu } from '../UI-CHAKRA/Menu/Menu';
import { MenuList } from '../UI-CHAKRA/Menu/MenuList';
import { Box } from '../UI-CHAKRA/Box';
import { Icon } from '@chakra-ui/react';
import { Grid } from '../UI-CHAKRA/Grid';
import { GridItem } from '../UI-CHAKRA/GridItem';
import { PrescriptionContext, PrescriptionContextProps } from '../../providers/PrescriptionProvider';
import { Skeleton } from '../UI-CHAKRA/Skeleton';
import { SEARCH_TYPE, SEARCH_TYPE_MAP } from './SearchTypeEnum';
import { Anchor } from '../notifications/Anchor';
import { ANCHORS, ANCHOR_POS } from '../notifications/notifications';
import { Flex } from '../UI-CHAKRA/Flex';
import { usePrescriptionStore } from '../../stores/prescriptionStore';

export interface ItemContent {
  type: SEARCH_TYPE;
  label: string;
  icon: string;
  searchable: boolean;
  visible: boolean;
}

interface OptionButtonProps {
  buttonData: ItemContent;
  screenLimit: number;
  isLast: boolean;
  onClick(): void;
  isSelected: boolean;
  primaryColor: string;
}

interface ElementStats {
  offsetLeft: number;
  clientWidth: number;
}

const OptionButton: FunctionComponent<OptionButtonProps> = ({
  buttonData,
  screenLimit,
  isLast,
  onClick,
  isSelected,
}) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [elementScreenStats, setElementScreenStats] = useState<ElementStats>({ offsetLeft: 0, clientWidth: 0 });

  const isLastOffset = useMemo(() => {
    return isLast ? 0 : 100; // 100 é o tamanho do botão de Outros que não é aplicado para o último elemento.
  }, [isLast]);

  const canFitOnScreen = useMemo(() => {
    return elementScreenStats.offsetLeft + elementScreenStats.clientWidth + isLastOffset < screenLimit;
  }, [elementScreenStats, screenLimit, isLastOffset]);

  useEffect(() => {
    if (!isVisible) return;

    setElementScreenStats({
      offsetLeft: gridRef?.current?.offsetLeft || 0,
      clientWidth: gridRef?.current?.clientWidth || 0,
    });
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      const offsetLeft = gridRef?.current?.offsetLeft || 0;
      const clientWidth = gridRef?.current?.clientWidth || 0;

      setIsVisible(offsetLeft + clientWidth + isLastOffset < screenLimit);
    } else if (canFitOnScreen) {
      setIsVisible(true);
    }
  }, [isVisible, screenLimit, canFitOnScreen, isLastOffset]);

  return isVisible ? (
    <div ref={gridRef}>
      <GridItem
        className={`option-grid ${isSelected && 'active'}`}
        onClick={onClick}
        data-cy={`option-grid-${buttonData.type}`}
      >
        <Anchor anchorName={ANCHORS[SEARCH_TYPE_MAP[buttonData.type]]} position={ANCHOR_POS.BOTTOM}>
          <StyledButton variant={`${isSelected ? 'solid' : 'ghost'}`} className={`${isSelected && 'active'}`}>
            <ReactSVG
              src={buttonData.icon}
              style={{
                marginRight: '10px',
              }}
              data-testid={buttonData.type}
            />
            {buttonData.label}
          </StyledButton>
        </Anchor>
      </GridItem>
    </div>
  ) : (
    <div />
  );
};

interface SearchTypesProps {
  onChange(item: ItemContent): void;
  canAddExam: boolean;
  canAddMedicalCertificate: boolean;
  canAddMedicalReferral: boolean;
  selectedSearchType: SEARCH_TYPE;
  primaryColor: string;
  canAddMedicalReport: boolean;
  canShowMedicalPrescription: boolean;
  canShowFormula: boolean;
  canShowPrescription?: boolean;
}

interface IScreenData {
  widthLimit: number;
  lastGridPosition: number;
}

function SearchTypes(props: SearchTypesProps) {
  const { prescriptionLoading } = useContext<PrescriptionContextProps>(PrescriptionContext);

  const {
    canAddExam,
    canAddMedicalCertificate,
    selectedSearchType,
    canAddMedicalReferral,
    onChange,
    canAddMedicalReport,
    canShowMedicalPrescription,
    canShowFormula,
    canShowPrescription,
  } = props;

  const [screenData, setScreenData] = useState<IScreenData>({
    widthLimit: 0,
    lastGridPosition: 0,
  });
  const [collapseButtons, setCollapseButtons] = useState<ItemContent[]>([]);
  const prescriberConfigurations = usePrescriptionStore((store) => store.prescriberConfiguration);

  const searchTypeContent = useMemo(() => {
    return [
      {
        type: SEARCH_TYPE.MEDICINE,
        label: canShowMedicalPrescription ? SEARCH_TYPE.MEDICINE : ('Receita' as SEARCH_TYPE),
        icon: MedicineIcon,
        searchable: true,
        visible: canShowPrescription,
      },
      {
        type: SEARCH_TYPE.FORMULA,
        label: canShowFormula ? SEARCH_TYPE.FORMULA : ('Receita' as SEARCH_TYPE),
        icon: FormulaIcon,
        searchable: true,
        visible: canShowFormula,
      },
      {
        type: SEARCH_TYPE.EXAMS,
        label: prescriberConfigurations?.Features?.ExamsLabel ? SEARCH_TYPE.EXAMS_ONLY : SEARCH_TYPE.EXAMS,
        icon: ExamIcon,
        searchable: true,
        visible: canAddExam,
      },
      {
        type: SEARCH_TYPE.MEDICAL_CERTIFICATE,
        label: SEARCH_TYPE.MEDICAL_CERTIFICATE,
        icon: MedicalCertificateIcon,
        searchable: false,
        visible: canAddMedicalCertificate,
      },
      {
        type: SEARCH_TYPE.MEDICAL_REFERRAL,
        label: SEARCH_TYPE.MEDICAL_REFERRAL,
        icon: MedicalReferralIcon,
        searchable: false,
        visible: canAddMedicalReferral,
      },
      {
        type: SEARCH_TYPE.MEDICAL_REPORT,
        label: SEARCH_TYPE.MEDICAL_REPORT,
        icon: MedicalReportIcon,
        searchable: false,
        visible: canAddMedicalReport,
      },
    ];
  }, [
    canAddExam,
    canAddMedicalCertificate,
    canAddMedicalReferral,
    canAddMedicalReport,
    canShowMedicalPrescription,
    canShowPrescription,
    canShowFormula,
    prescriberConfigurations?.Features?.ExamsLabel,
  ]);

  const visibleItems = useMemo(() => {
    return searchTypeContent.filter((item) => item.visible);
  }, [searchTypeContent]);

  useEffect(() => {
    const allGrids = document.getElementsByClassName('option-grid');

    if (!visibleItems) return;

    const lastGrid = allGrids[allGrids.length - 1];
    const lastGridPos = get(lastGrid, 'getBoundingClientRect().x', 0) + get(lastGrid, 'clientWidth', 0);

    setScreenData({
      widthLimit: window.innerWidth,
      lastGridPosition: screenData.lastGridPosition || lastGridPos,
    });
  }, [screenData.lastGridPosition, visibleItems]);

  useEffect(() => {
    const rootContainer = document.getElementById('Container');

    if (!rootContainer || !visibleItems) return;

    let resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }
          setScreenData({
            widthLimit: entry.contentRect.width,
            lastGridPosition: screenData.lastGridPosition,
          });
        });
      });
    });

    resizeObserver.observe(rootContainer);

    return () => resizeObserver.disconnect();
  }, [screenData.lastGridPosition, visibleItems]);

  useEffect(() => {
    const allGridLength = document.getElementsByClassName('option-grid').length;

    setCollapseButtons(visibleItems.slice(allGridLength) as ItemContent[]);
  }, [screenData, visibleItems]);

  const handleChange = useCallback(
    (item: ItemContent) => {
      if (item.type === selectedSearchType) return;

      onChange(item);
    },
    [onChange, selectedSearchType]
  );

  const SearchOptions = useMemo(() => {
    return (
      <Flex gap={'16px'}>
        {visibleItems.map((item, stIndex) => {
          return (
            <OptionButton
              key={item.type}
              buttonData={item as ItemContent}
              screenLimit={screenData.widthLimit}
              isLast={stIndex === visibleItems.length - 1}
              onClick={() => handleChange(item as ItemContent)}
              isSelected={selectedSearchType === item.type}
              primaryColor={props.primaryColor}
            />
          );
        })}
      </Flex>
    );
  }, [handleChange, screenData.widthLimit, selectedSearchType, visibleItems, props.primaryColor]);

  const isCollapsedButtonSelected = useMemo(() => {
    const collapsedTypes = collapseButtons.map((item) => item.type);

    return collapsedTypes.includes(selectedSearchType);
  }, [collapseButtons, selectedSearchType]);

  if (prescriptionLoading) {
    return (
      <Box mb={'12px'} display={'flex'}>
        <Skeleton
          height={'55px'}
          width={'160px'}
          variant={'rect'}
          marginBottom={'4px'}
          borderRadius={'20px'}
          marginRight={'16px'}
        />
        <Skeleton
          height={'55px'}
          width={'160px'}
          variant={'rect'}
          marginBottom={'4px'}
          borderRadius={'20px'}
          marginRight={'16px'}
        />
        <Skeleton height={'55px'} width={'160px'} variant={'rect'} marginBottom={'4px'} borderRadius={'20px'} />
      </Box>
    );
  }

  return (
    <Grid
      justifyContent={{ base: 'space-between', md: 'flex-start' }}
      flexWrap={'nowrap'}
      data-tut="search-type-select"
    >
      {SearchOptions}
      {!isEmpty(collapseButtons) && !isEmpty(visibleItems) && (
        <GridItem textAlign={'right'}>
          <Menu isLazy>
            <StyledMenuButton className={`${isCollapsedButtonSelected && 'active'}`}>
              <Box w={'100%'} h={'100%'} display={'flex'} alignItems={'center'}>
                <Icon as={MdMenu} fontSize={'18px'} />
              </Box>
            </StyledMenuButton>
            <MenuList
              borderRadius={'20px'}
              zIndex={10}
              p={'10px'}
              display={'flex'}
              gap={'5px'}
              flexDirection={'column'}
            >
              {collapseButtons.map((collapseButton) => (
                <StyledMenuItem
                  key={collapseButton.type}
                  onClick={() => {
                    handleChange(collapseButton);
                  }}
                  className={`${collapseButton.type === selectedSearchType && 'active'}`}
                >
                  <FaPlus style={{ width: '12px' }} />
                  <span style={{ marginLeft: '8px' }}>{collapseButton.label}</span>
                </StyledMenuItem>
              ))}
            </MenuList>
          </Menu>
        </GridItem>
      )}
    </Grid>
  );
}

export default React.memo(SearchTypes);
